import React from 'react';
import { METRIC_WEIGHTS } from '../../../config/scoring';
import { formatWater } from '../../utils/unitConversions';

/**
 * Shared daily-metrics logic — the derivation layer behind the calendar
 * tooltip AND the mobile DailyCard. Pure functions over one day's health
 * data: metric selection, tier coloring, tier/weight sorting, progress-bar
 * rendering. Presentation (tooltip popup vs. mobile tile) lives in the
 * consumers; this module owns the "what to show and how it scores" logic.
 *
 * Extracted from CalendarDayTooltip (2026-05-31) so the behavior is shared
 * and validated against the existing tooltip before reuse elsewhere.
 */

export interface DailyMetricsData {
  date: string;
  actual_values?: {
    weight?: number;
    steps?: number;
    calories?: number;
    sleep_hours?: number;
    water_intake?: number;
    energy_level?: number;
    stress_level?: number;
    [key: string]: number | undefined;
  };
  weight?: number;
  steps?: number;
  calories?: number;
  sleep?: number;
  water?: number;
  workouts?: number;
  energy_level?: number;
  stress_level?: number;
  notes?: string;
  score?: number;
  status?: string;
  targets_at_time?: Record<string, any>;
  active_goals_snapshot?: string[];
  tierStatus?: 'tier3' | 'tier2' | 'tier1' | 'none';
  softScore?: number | null;
}

export interface DailyMetricsOptions {
  showAllMetrics?: boolean;
  userGoals?: any;
  isCoachView?: boolean;
}

export interface MetricConfig {
  templateId: string;
  dataField: string;
  displayName: string;
  icon: string;
  defaultTarget: number;
  unit: string;
  targetField?: string;
  renderProgressBar: (value: number, target: number, targetType?: string, variance?: number) => JSX.Element;
}

const GRAY = '#d1d5db';

const progressBar = (percentage: number, color: string): JSX.Element => (
  <div style={{ width: '100%', height: '4px', backgroundColor: 'var(--bg-secondary)', borderRadius: '2px', overflow: 'hidden' }}>
    <div style={{ width: `${percentage}%`, height: '100%', backgroundColor: color, transition: 'width 0.3s ease' }} />
  </div>
);

export const METRICS_CONFIG: MetricConfig[] = [
  {
    templateId: 'daily_steps',
    dataField: 'steps',
    displayName: 'Steps',
    icon: '👟',
    defaultTarget: 10000,
    unit: '',
    targetField: 'steps',
    renderProgressBar: (value, target) => {
      const percentage = Math.min(100, (value / target) * 100);
      const color = value >= target ? '#10b981' : value >= target * 0.75 ? '#84cc16' : value >= target * 0.5 ? '#f59e0b' : '#ef4444';
      return progressBar(percentage, color);
    }
  },
  {
    templateId: 'daily_water',
    dataField: 'water_intake',
    displayName: 'Water',
    icon: '💧',
    defaultTarget: 64,
    unit: 'oz',
    targetField: 'water_intake',
    renderProgressBar: (value, target) => {
      const percentage = Math.min(100, (value / target) * 100);
      const color = value >= target ? '#10b981' : value >= target * 0.75 ? '#84cc16' : value >= target * 0.5 ? '#f59e0b' : '#ef4444';
      return progressBar(percentage, color);
    }
  },
  {
    templateId: 'daily_sleep',
    dataField: 'sleep_hours',
    displayName: 'Sleep',
    icon: '😴',
    defaultTarget: 8,
    unit: 'hrs',
    targetField: 'sleep_hours',
    renderProgressBar: (value, target) => {
      const percentage = Math.min(100, (value / target) * 100);
      const color = value >= target * 0.95 ? '#10b981' : value >= target * 0.8 ? '#84cc16' : value >= target * 0.65 ? '#f59e0b' : '#ef4444';
      return progressBar(percentage, color);
    }
  },
  {
    templateId: 'daily_calories',
    dataField: 'calories',
    displayName: 'Calories',
    icon: '🔥',
    defaultTarget: 2200,
    unit: '',
    targetField: 'calories',
    renderProgressBar: (value, target, targetType = 'target', variance = 10) => {
      let backgroundColor = '#f59e0b';
      const percentage = Math.min(100, (value / target) * 100);

      if (targetType === 'target') {
        const varianceAmount = target * (variance / 100);
        const lowerBound = target - varianceAmount;
        const upperBound = target + varianceAmount;

        if (value >= lowerBound && value <= upperBound) {
          backgroundColor = '#10b981';
        } else if (value >= lowerBound * 0.9 && value <= upperBound * 1.1) {
          backgroundColor = '#84cc16';
        } else if (value >= lowerBound * 0.8 && value <= upperBound * 1.2) {
          backgroundColor = '#f59e0b';
        } else {
          backgroundColor = '#ef4444';
        }
      } else if (targetType === 'over') {
        backgroundColor = value >= target ? '#10b981' : value >= target * 0.75 ? '#84cc16' : '#f59e0b';
      } else if (targetType === 'under') {
        backgroundColor = value <= target ? '#10b981' : value <= target * 1.25 ? '#84cc16' : '#f59e0b';
      }

      return progressBar(percentage, backgroundColor);
    }
  }
];

const TARGETS_FIELD_MAP: Record<string, string> = {
  'daily_steps': 'steps',
  'daily_water': 'water_intake',
  'daily_sleep': 'sleep_hours',
  'daily_calories': 'calories'
};

const WEIGHT_FIELD_MAP: Record<string, string> = {
  'daily_calories': 'calories',
  'daily_protein': 'protein_g',
  'daily_carbs': 'carbs_g',
  'daily_fats': 'fats_g',
  'daily_sodium': 'sodium_mg',
  'daily_sugar': 'sodium_mg',
  'daily_fiber': 'fiber_g',
  'daily_steps': 'steps',
  'daily_water': 'water_intake',
  'daily_sleep': 'sleep_hours',
  'daily_energy': 'energy_level',
  'daily_stress': 'stress_level',
};

const ACTIVE_FIELD_MAP: Record<string, string> = {
  'daily_steps': 'steps',
  'daily_water': 'water_intake',
  'daily_sleep': 'sleep_hours',
  'daily_calories': 'calories',
  'daily_energy': 'energy_level',
  'daily_stress': 'stress_level'
};

/**
 * Build the full derivation API for one day's data + view options.
 * Returns the same helpers the tooltip used internally, now shared.
 */
export function getDailyMetrics(data: DailyMetricsData, options: DailyMetricsOptions = {}) {
  const { showAllMetrics = false, userGoals = null, isCoachView = false } = options;

  const getMetricValue = (field: string): number | undefined => {
    if (data.actual_values && data.actual_values[field as keyof typeof data.actual_values] !== undefined) {
      return data.actual_values[field as keyof typeof data.actual_values];
    }
    const legacyFieldMap: Record<string, string> = {
      'sleep_hours': 'sleep',
      'water_intake': 'water',
      'weight': 'weight'
    };
    const legacyField = legacyFieldMap[field] || field;
    return data[legacyField as keyof DailyMetricsData] as number | undefined;
  };

  const getGoalTierColor = (goalTemplateId: string): string => {
    if (!data.targets_at_time) return GRAY;
    const targetsField = TARGETS_FIELD_MAP[goalTemplateId];
    if (!targetsField) return GRAY;
    const historicalGoal = data.targets_at_time[targetsField] as any;
    if (!historicalGoal) return GRAY;

    if (!isCoachView) {
      if (historicalGoal.visibility === 'hidden') return GRAY;
      if (!showAllMetrics) return GRAY;
      if (historicalGoal.visibility === 'visible' && historicalGoal.status === 'enabled') return '#4CAF50';
      if (historicalGoal.visibility === 'visible' && historicalGoal.status === 'disabled') return '#00BCD4';
    }

    if (historicalGoal.visibility === 'visible' && historicalGoal.status === 'enabled') return '#4CAF50';
    if (historicalGoal.visibility === 'visible' && historicalGoal.status === 'disabled') return '#00BCD4';
    if (historicalGoal.visibility === 'hidden') return '#FF9800';
    return GRAY;
  };

  const getGoalTier = (goalTemplateId: string): number => {
    if (!data.targets_at_time) return 0;
    const targetsField = TARGETS_FIELD_MAP[goalTemplateId];
    if (!targetsField) return 0;
    const historicalGoal = data.targets_at_time[targetsField] as any;
    if (!historicalGoal) return 0;
    if (historicalGoal.visibility === 'visible' && historicalGoal.status === 'enabled') return 3;
    if (historicalGoal.visibility === 'visible' && historicalGoal.status === 'disabled') return 2;
    if (historicalGoal.visibility === 'hidden') return 1;
    return 0;
  };

  const getGoalWeight = (goalTemplateId: string): number => {
    const field = WEIGHT_FIELD_MAP[goalTemplateId];
    return field ? ((METRIC_WEIGHTS as Record<string, number>)[field] ?? 0) : 0;
  };

  const isGoalActive = (goalTemplateId: string): boolean => {
    const dataField = ACTIVE_FIELD_MAP[goalTemplateId];
    if (!dataField) return false;

    const hasData = getMetricValue(dataField) !== undefined;
    if (!hasData) return false;

    if (showAllMetrics) return true;

    if (isCoachView && userGoals) {
      const hasGoal = Object.values(userGoals).some((goal: any) => goal.template_id === goalTemplateId);
      if (!hasGoal) return false;

      if (data.targets_at_time) {
        const targetsField = TARGETS_FIELD_MAP[goalTemplateId];
        if (targetsField) {
          return !!data.targets_at_time[targetsField];
        }
      }
      return false;
    }

    if (data.targets_at_time) {
      const targetsField = TARGETS_FIELD_MAP[goalTemplateId];
      if (targetsField && data.targets_at_time[targetsField]) {
        const historicalGoal = data.targets_at_time[targetsField] as any;
        return historicalGoal.visibility === 'visible';
      }
    }

    return false;
  };

  const getSortedMetrics = (): MetricConfig[] => {
    return METRICS_CONFIG
      .filter(metric => isGoalActive(metric.templateId))
      .sort((a, b) => {
        const tierA = getGoalTier(a.templateId);
        const tierB = getGoalTier(b.templateId);
        if (tierA !== tierB) return tierB - tierA;
        const weightA = getGoalWeight(a.templateId);
        const weightB = getGoalWeight(b.templateId);
        return weightB - weightA;
      });
  };

  return {
    getMetricValue,
    getGoalTierColor,
    getGoalTier,
    getGoalWeight,
    isGoalActive,
    getSortedMetrics,
    GRAY,
  };
}

export function formatDailyDate(dateString: string): string {
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric'
  });
}

export { formatWater };
